import styled from 'styled-components';
import { Stack } from '../../../../../../Stack';

export const DEFAULT_LINE_ITEM_DESCRIPTION_BOTTOM_MARGIN = 8;

// Hack:
// In Figma design, we use 0px 0px 1px rgba(0, 0, 0, 0.08), 0px 2px 4px rgba(0, 0, 0, 0.16);
// But we have to use "Tracking Details Box font color - secondary" - #888888 from requirement
// -> Need to support opacity of box-shadow color, so we convert 6 digits hex to 8 digits hex (rrggbbaa) where aa is alpha
// Browser support: https://caniuse.com/css-rrggbbaa
// Opacity conversion table: https://css-tricks.com/8-digit-hex-codes/, or
export const ItemBox = styled.div`
  box-shadow: ${({ theme }) =>
    `
     0px 0px 1px ${theme.configs.tertiaryBox.secondaryFontColor.concat('14')},
     0px 2px 4px ${theme.configs.tertiaryBox.secondaryFontColor.concat('29')}
    `};
  background-color: ${({ theme }) => theme.configs.widget.bgColor};
  overflow: hidden;
  border-radius: 4px;
`;

// TODO: Update color from config
export const BackgroundShape = styled.div`
  background-color: #d9d9d9;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const Thumbnail = styled.img<{
  width: string | number;
  height: string | number;
  isRounded: boolean;
}>`
  height: ${({ height }) =>
    typeof height === 'string' ? `${height}` : `${height}px`};
  width: ${({ width }) =>
    typeof width === 'string' ? `${width}` : `${width}px`};
  border-radius: ${({ isRounded }) => (isRounded ? '50%' : '0')};
  object-fit: contain;
`;

export const DetailsWrapper = styled(ItemBox)<{ isTwoColumns: boolean }>`
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  transform: rotateY(0deg);
  transform-style: preserve-3d;
  backface-visibility: hidden;
  display: flex;
  flex-direction: ${({ isTwoColumns }) => (isTwoColumns ? 'row' : 'column')};
  justify-content: ${({ isTwoColumns }) =>
    isTwoColumns ? 'flex-start' : 'space-between'};
`;

export const DescriptionWrapper = styled(ItemBox)`
  width: 100%;
  height: 100%;
  position: absolute;
  box-sizing: border-box;
  padding: 16px 12px 0 12px;
  box-shadow: ${({ theme }) =>
    `
    0px 2px 4px ${theme.configs.tertiaryBox.secondaryFontColor.concat('0a')},
    0px 8px 16px ${theme.configs.tertiaryBox.secondaryFontColor.concat('29')};
  `};
  transform: rotateY(180deg);
  transform-style: preserve-3d;
  backface-visibility: hidden;
`;

export const DescriptionRow = styled(Stack)<{ bottomMargin?: number }>`
  ${({ bottomMargin = DEFAULT_LINE_ITEM_DESCRIPTION_BOTTOM_MARGIN }) =>
    bottomMargin && `margin-bottom: ${bottomMargin}px`};
`;

export const DescriptionColumn = styled(DescriptionRow).attrs({
  direction: 'column',
})`
  ${({ bottomMargin = DEFAULT_LINE_ITEM_DESCRIPTION_BOTTOM_MARGIN }) =>
    bottomMargin && `margin-bottom: ${bottomMargin}px`};
`;

export const DescriptionValue = styled.div<{ isInline?: boolean }>`
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  color: ${({ theme }) => theme.configs.tertiaryBox.mainFontColor};
  ${({ isInline }) => isInline && `display: inline`};
  overflow-wrap: break-word;
  word-break: break-all;
`;

export const DescriptionTitle = styled(DescriptionValue)`
  font-weight: 700;
`;

export const TruncatedTitle = styled(DescriptionTitle)<{ isShowFull: boolean }>`
  overflow: hidden;
  text-overflow: ellipsis;
  display: ${({ isShowFull }) => (isShowFull ? 'block' : '-webkit-box')};
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

export const DescriptionColoredValue = styled(DescriptionValue)`
  color: ${({ theme }) => theme.configs.CTAButton.bgColor};
`;

export const DescriptionColoredButton = styled(DescriptionColoredValue).attrs({
  as: 'button',
  role: 'button',
  tabIndex: 0,
})`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  width: fit-content;
  font-family: inherit;
`;

export const DescriptionHyperLink = styled.a<{ href?: string }>`
  &&& {
    font-weight: 400;
    font-size: 14px;
    line-height: 18px;
    color: ${({ theme }) => theme.configs.CTAButton.bgColor};
    text-decoration: ${({ href }) => href && 'underline'};
  }
`;
